import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] transition-all duration-[140ms] focus:outline-none focus:ring-[3px] focus:ring-primary/10 focus:ring-offset-0",
  {
    variants: {
      variant: {
        // Default (Burgundy)
        default:
          "bg-primary text-primary-foreground border border-primary hover:bg-primary/90",

        // Premium — wider tracking, subtle shadow
        premium:
          "bg-primary text-primary-foreground border border-primary tracking-[0.1em] shadow-[var(--shadow-warm-sm)]",

        // Secondary
        secondary:
          "bg-secondary text-secondary-foreground border border-border hover:bg-secondary/90",

        // Destructive/Alert
        destructive:
          "bg-destructive text-destructive-foreground border border-destructive hover:bg-destructive/90",

        // Highlight Label (Left border accent)
        highlight:
          "bg-primary/[0.06] text-primary border-l-[2.5px] border-l-primary border-y-0 border-r-0 font-bold normal-case px-2 py-1",

        // Outline
        outline:
          "text-foreground border border-border bg-transparent hover:bg-muted",

        // Success — tinted bg for better contrast
        success:
          "bg-success/10 text-[#065f46] border border-success/30 dark:text-[#6ee7b7]",

        // Warning — new in v2.1
        warning:
          "bg-[var(--status-warning-bg)] text-[#92400e] border border-[var(--status-warning-border)] dark:text-[#fbbf24]",

        // Status: Open — new in v2.1
        "status-open":
          "bg-[var(--status-open-bg)] text-[var(--status-open)] border border-[var(--status-open-border)]",

        // Status: Applied — new in v2.1
        "status-applied":
          "bg-[var(--status-applied-bg)] text-[var(--status-applied)] border border-[var(--status-applied-border)]",

        // Status: Closed — new in v2.1
        "status-closed":
          "bg-[var(--status-closed-bg)] text-[var(--status-closed)] border border-[var(--status-closed-border)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
